import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { FirestoreService } from '../../data/firestore.service';
import { Team } from 'src/app/data/types';
import { Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

interface OrganizationGroup {
  name: string;
  teams: Team[];
}

@Component({
  selector: 'app-team-list',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterModule],
  templateUrl: './team-list-recordings.component.html',
  styleUrls: ['./team-list-recordings.component.scss']
})
export class TeamListComponent implements OnInit, OnDestroy {
  // State
  isLoading = signal(true);
  groupedTeams = signal<OrganizationGroup[]>([]);
  
  // Cleanup
  private ngUnsubscribe = new Subject<void>();

  constructor(private db: FirestoreService, private titleService: Title, private translate: TranslateService){
    this.titleService.setTitle(`${this.translate.instant('team-list.title')} | ${environment.appName}`);
  }

  ngOnInit() {
    this.loadTeams();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  loadTeams() {
    this.isLoading.set(true);

    // Using your specific method to fetch teams
    this.db.listUserData<Team>('teams', 'm2m')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (teams) => {
          if (!teams) teams = [];
          this.processTeams(teams);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Error fetching teams', err);
          this.isLoading.set(false);
        }
      });
  }

  private processTeams(teams: Team[]) {
    const groups: { [key: string]: Team[] } = {};

    teams.forEach(team => {
      // Extract Organization Name from the Reference object (.d)
      // Fallback to 'Other' if missing
      const orgName = team.organization?.d || 'Other';

      if (!groups[orgName]) {
        groups[orgName] = [];
      }
      groups[orgName].push(team);
    });

    // Convert to Array and Sort
    const result: OrganizationGroup[] = Object.keys(groups).map(orgName => {
      // 1. Sort teams within the group (A-Z)
      const sortedTeams = groups[orgName].sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      
      return {
        name: orgName,
        teams: sortedTeams
      };
    });

    // 2. Sort Organizations (A-Z)
    result.sort((a, b) => a.name.localeCompare(b.name));

    this.groupedTeams.set(result);
  }
}